import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfBadge, EmptyState, Metric, PageHeader, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function PanchatanthraCycles() {
  const { tenantId, can, access } = useAuth();
  const [cycles, setCycles] = useState([]);
  const [versions, setVersions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", period_label: "", framework_version_id: "", confidentiality: "1" });

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/panchatanthra/cycles`).then(({ data }) => setCycles(data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/tenants/${tenantId}/panchatanthra/framework-versions`).then(({ data }) => setVersions(data)).catch(() => {});
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  const create = async () => {
    try {
      await api.post(`/tenants/${tenantId}/panchatanthra/cycles`, {
        ...form,
        confidentiality: Number(form.confidentiality),
        target_profile: { REV: 4, FIN: 4, LEAD: 4, BMOD: 4, TRUST: 4 },
      });
      toast.success("Cycle created and pinned to the published version");
      setOpen(false);
      setForm({ name: "", period_label: "", framework_version_id: "", confidentiality: "1" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const published = versions.filter((v) => v.state === "published");

  return (
    <div className="space-y-10">
      <PageHeader
        testid="panchatanthra-header"
        eyebrow="Framework · Panchatanthra"
        title="Assessment cycles"
        description="Every cycle is pinned to a published questionnaire version so scores stay reproducible."
        actions={
          can("create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-cycle-button" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">
                  New cycle
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">New assessment cycle</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="label-caps">Cycle name</Label>
                    <Input data-testid="cycle-name-input" className="rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Period label</Label>
                    <Input data-testid="cycle-period-input" className="rounded-none" value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Questionnaire version (published only)</Label>
                    <Select value={form.framework_version_id} onValueChange={(v) => setForm({ ...form, framework_version_id: v })}>
                      <SelectTrigger data-testid="cycle-version-select" className="rounded-none"><SelectValue placeholder="Select version" /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {published.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Confidentiality</Label>
                    <Select value={form.confidentiality} onValueChange={(v) => setForm({ ...form, confidentiality: v })}>
                      <SelectTrigger data-testid="cycle-confidentiality-select" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {[1, 2, 3].filter((l) => l <= (access?.confidentiality_max || 1)).map((l) => (
                          <SelectItem key={l} value={String(l)}>{["", "General", "Sensitive", "Restricted"][l]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="create-cycle-submit" className="w-full rounded-none" onClick={create} disabled={!form.name || !form.framework_version_id}>
                    Create cycle
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        <Metric testid="pt-metric-cycles" label="Cycles" value={cycles.length} />
        <Metric testid="pt-metric-versions" label="Published versions" value={published.length} hint="Immutable once published" />
        <Metric
          testid="pt-metric-open"
          label="Open for response"
          value={cycles.filter((c) => c.status === "in_progress").length}
        />
      </div>

      <div className="panel divide-y divide-border" data-testid="cycles-list">
        {cycles.map((c) => (
          <Link
            key={c.id}
            to={`/panchatanthra/cycles/${c.id}`}
            data-testid={`cycle-row-${c.id}`}
            className="flex flex-wrap items-center justify-between gap-4 px-5 py-5 transition-colors hover:bg-muted"
          >
            <div className="min-w-0">
              <p className="font-display text-lg">{c.name}</p>
              <p className="label-caps mt-1">
                {c.period_label || "—"} · version {c.version_label} · {c.progress?.submitted}/{c.progress?.assignments} respondents submitted
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs tabular-nums text-muted-foreground">{c.progress?.answers} answers</span>
              <ConfBadge level={c.confidentiality} />
              <StateBadge state={c.status} />
            </div>
          </Link>
        ))}
        {cycles.length === 0 && <EmptyState testid="cycles-empty" title="No cycles" message="Create an assessment cycle pinned to a published questionnaire version." />}
      </div>
    </div>
  );
}
